import React, { useEffect, useMemo, useState } from "react";
import { ArrayProvider, useArrayContext } from "../../../state/ArrayContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { SearchDifference, compareSearch } from "../../../diff/search-compare";
import { findFirstComponentOfType } from "../../internal/TextExtractor";

interface CompareArrayProps {
    category: string,
    children: React.ReactNode,
}

/***
* Compares an array of metadata (current) against another array of metadata (baseline) and
* returns the difference.
* 
* Params:
*    category: The name of the requested statistic. Available categories are:
*       - new: new metadata since the baseline
*       - removed: removed metadata since the baseline
*       - modified: modified metadata since the baseline
*/
export const CompareArray = React.memo(function CompareArray({ category, children }: CompareArrayProps): React.ReactElement {
    const { setArray } = useArrayContext();

    // Get the baseline child which should contain an array source for the baseline
    const baselineChild = useMemo(() => findFirstComponentOfType(children, "arg-baseline"), [children]);
    const [baselineArray, setBaselineArray] = useState<Array<SourceItem>>();

    // Get the current child which should contain an array source for current
    const currentChild = useMemo(() => findFirstComponentOfType(children, "arg-current"), [children]);
    const [currentArray, setCurrentArray] = useState<Array<SourceItem>>();

    useEffect(() => {
        if (baselineArray && currentArray) {
            const difference: SearchDifference = compareSearch(baselineArray, currentArray);
            let result: Array<SourceItem> = [];
            switch (category) {
                case "new":
                    result = difference.new.map((item, ix) => { return { ...item, position: ix + 1 }; });
                    break;
                case "removed":
                    result = difference.removed.map((item, ix) => { return { ...item, position: ix + 1 }; });
                    break;
                case "modified":
                    result = difference.modified.map((item, ix) => { return { ...item, position: ix + 1 }; });
                    break;
            }
            setArray(result);
        }
    }, [setArray, baselineArray, currentArray, category]);

    return <>
        <ArrayProvider setArray={setBaselineArray}>
            {baselineChild?.props.children}
        </ArrayProvider>
        <ArrayProvider setArray={setCurrentArray}>
            {currentChild?.props.children}
        </ArrayProvider>
    </>;
});